export const AGENT_MERGE_ACTIONS = ['fix_ci', 'resolve_conflicts', 'respond_to_reviewers'] as const
export const AGENT_MERGE_UNAVAILABLE_MESSAGE = 'Agent Merge is temporarily unavailable.'

export type AgentMergeAction = (typeof AGENT_MERGE_ACTIONS)[number]

export type AgentMergeConfiguration = {
  enabled: boolean
  allowedActions: AgentMergeAction[]
}

export type AgentMergePolicy = {
  automationsPolicyDisabled: boolean
  automationsPolicyDisabledByOrganization: boolean
}

/**
 * Identifies the user whose authority an enabled Agent Merge assignment runs under. A disabled
 * assignment can retain a stale `ownerLogin` for audit history, so ownership fields are only
 * meaningful while `enabled` is true.
 */
export type AgentMergeOwnership = {
  ownerLogin: string | null
  viewerOwnsAssignment: boolean
}

export const AGENT_MERGE_CHECK_SCOPE_STATES = ['all_required', 'configured', 'invalid', 'unavailable'] as const

export type AgentMergeCheckScopeState = (typeof AGENT_MERGE_CHECK_SCOPE_STATES)[number]

export type AgentMergeCheckScope = {
  state: AgentMergeCheckScopeState
  configPath: string | null
  configUrl: string | null
  checkNames: string[]
  unmatchedCheckNames: string[] | null
  actionsOnly: boolean
}

export const AGENT_MERGE_ENROLLMENT_STATES = ['pending', 'succeeded', 'skipped', 'failed', 'unconfirmed'] as const

export type AgentMergeEnrollmentState = (typeof AGENT_MERGE_ENROLLMENT_STATES)[number]

/**
 * Tracks a background PR-creation enrollment attempt that has not yet produced (or will never
 * produce) an authoritative configuration. The backend omits this field once an authoritative
 * configuration -- including a disabled one -- is returned, so its mere presence means the
 * `enabled`/`allowedActions` values alongside it are not yet confirmed and must not be rendered
 * as-is.
 */
export type AgentMergeEnrollmentAttempt = {
  requestId: string
  state: AgentMergeEnrollmentState
  requestedAt: string
  deadlineAt: string
  reason: string | null
}

export type AgentMergeConfigurationResponse = AgentMergeConfiguration &
  Partial<AgentMergePolicy> &
  AgentMergeOwnership & {
    headSha: string
    etag: string | null
    configurationGeneration?: string
    activityAvailable?: boolean
    fixCICheckScope: AgentMergeCheckScope | null
    automaticEnrollment?: AgentMergeEnrollmentAttempt
  }

type AgentMergeCheckScopeInput = {
  state?: string
  configPath?: unknown
  configUrl?: unknown
  checkNames?: readonly unknown[]
  unmatchedCheckNames?: readonly unknown[] | null
  actionsOnly?: unknown
}

type AgentMergeEnrollmentAttemptInput = {
  requestId?: unknown
  state?: unknown
  requestedAt?: unknown
  deadlineAt?: unknown
  reason?: unknown
}

type AgentMergeConfigurationResponseInput = Omit<
  AgentMergeConfigurationResponse,
  'allowedActions' | 'fixCICheckScope' | 'automaticEnrollment' | keyof AgentMergePolicy | keyof AgentMergeOwnership
> &
  Partial<AgentMergePolicy> &
  Partial<AgentMergeOwnership> & {
    allowedActions: readonly string[]
    fixCICheckScope?: AgentMergeCheckScopeInput | null
    automaticEnrollment?: AgentMergeEnrollmentAttemptInput | null
  }

export type AgentMergeErrorResponse = Partial<AgentMergePolicy> & {
  error?: string
  reason?: string
  automaticEnrollment?: AgentMergeEnrollmentAttemptInput | null
}

export class AgentMergeEnrollmentLoadError extends Error {
  readonly automaticEnrollment: AgentMergeEnrollmentAttempt

  constructor(message: string, automaticEnrollment: AgentMergeEnrollmentAttempt) {
    super(message)
    this.name = 'AgentMergeEnrollmentLoadError'
    this.automaticEnrollment = automaticEnrollment
  }
}

export function unresolvedAgentMergeEnrollment(
  data: AgentMergeConfigurationResponse | undefined,
  error: Error | null,
): AgentMergeEnrollmentAttempt | undefined {
  if (data?.etag) return undefined
  if (error) return error instanceof AgentMergeEnrollmentLoadError ? error.automaticEnrollment : undefined
  return data?.automaticEnrollment
}

export const DEFAULT_AGENT_MERGE_ACTION: AgentMergeAction = 'fix_ci'
export const AUTOMATIONS_POLICY_DISABLED_REASON = 'automations_policy_disabled'
export const UNAVAILABLE_AGENT_MERGE_CHECK_SCOPE: AgentMergeCheckScope = {
  state: 'unavailable',
  configPath: null,
  configUrl: null,
  checkNames: [],
  unmatchedCheckNames: null,
  actionsOnly: true,
}

export class AgentMergePolicyDisabledError extends Error {
  readonly policy: Pick<AgentMergePolicy, 'automationsPolicyDisabled'> &
    Partial<Pick<AgentMergePolicy, 'automationsPolicyDisabledByOrganization'>>

  constructor(
    message: string,
    policy: Pick<AgentMergePolicy, 'automationsPolicyDisabled'> &
      Partial<Pick<AgentMergePolicy, 'automationsPolicyDisabledByOrganization'>>,
  ) {
    super(message)
    this.name = 'AgentMergePolicyDisabledError'
    this.policy = policy
  }
}

function isAgentMergeAction(action: string): action is AgentMergeAction {
  return AGENT_MERGE_ACTIONS.some(candidate => candidate === action)
}

function isAgentMergeCheckScopeState(state: string): state is AgentMergeCheckScopeState {
  return AGENT_MERGE_CHECK_SCOPE_STATES.some(candidate => candidate === state)
}

function isAgentMergeEnrollmentState(state: string): state is AgentMergeEnrollmentState {
  return AGENT_MERGE_ENROLLMENT_STATES.some(candidate => candidate === state)
}

/**
 * Fails closed: any malformed attempt normalizes to `undefined` (no attempt) rather than a
 * guessed state, so a backend bug cannot strand the UI in a bogus "setting up" placeholder.
 */
export function normalizeAgentMergeEnrollmentAttempt(
  attempt: AgentMergeEnrollmentAttemptInput | null | undefined,
): AgentMergeEnrollmentAttempt | undefined {
  if (
    !attempt ||
    typeof attempt.requestId !== 'string' ||
    typeof attempt.state !== 'string' ||
    !isAgentMergeEnrollmentState(attempt.state) ||
    typeof attempt.requestedAt !== 'string' ||
    typeof attempt.deadlineAt !== 'string'
  ) {
    return undefined
  }

  return {
    requestId: attempt.requestId,
    state: attempt.state,
    requestedAt: attempt.requestedAt,
    deadlineAt: attempt.deadlineAt,
    reason: typeof attempt.reason === 'string' ? attempt.reason : null,
  }
}

export function normalizeAgentMergeCheckScope(
  scope: AgentMergeCheckScopeInput | null | undefined,
): AgentMergeCheckScope | null {
  if (!scope?.state || !isAgentMergeCheckScopeState(scope.state)) return null

  const checkNames =
    scope.state === 'configured'
      ? [
          ...new Set(
            (scope.checkNames ?? [])
              .filter((name): name is string => typeof name === 'string')
              .map(name => name.trim())
              .filter(Boolean),
          ),
        ]
      : []
  const checkNameSet = new Set(checkNames)
  const unmatchedCheckNames =
    scope.state === 'configured' && Array.isArray(scope.unmatchedCheckNames)
      ? [
          ...new Set(
            scope.unmatchedCheckNames
              .filter((name): name is string => typeof name === 'string')
              .map(name => name.trim())
              .filter(name => checkNameSet.has(name)),
          ),
        ]
      : null

  return {
    state: scope.state,
    configPath: typeof scope.configPath === 'string' ? scope.configPath : null,
    configUrl: typeof scope.configUrl === 'string' ? scope.configUrl : null,
    checkNames,
    unmatchedCheckNames,
    actionsOnly: typeof scope.actionsOnly === 'boolean' ? scope.actionsOnly : true,
  }
}

export function normalizeAgentMergePolicy(
  response: Partial<AgentMergePolicy>,
): Pick<AgentMergePolicy, 'automationsPolicyDisabled'> &
  Partial<Pick<AgentMergePolicy, 'automationsPolicyDisabledByOrganization'>> {
  return {
    automationsPolicyDisabled: response.automationsPolicyDisabled === true,
    ...(response.automationsPolicyDisabledByOrganization === undefined
      ? {}
      : {automationsPolicyDisabledByOrganization: response.automationsPolicyDisabledByOrganization === true}),
  }
}

export function normalizeAgentMergeConfiguration(
  response: AgentMergeConfigurationResponseInput,
): AgentMergeConfigurationResponse {
  const allowedActions = [...new Set(response.allowedActions.filter(isAgentMergeAction))]
  const policy =
    response.automationsPolicyDisabled === undefined && response.automationsPolicyDisabledByOrganization === undefined
      ? {}
      : normalizeAgentMergePolicy(response)

  return {
    ...response,
    ...policy,
    allowedActions: allowedActions.length > 0 ? allowedActions : [DEFAULT_AGENT_MERGE_ACTION],
    ownerLogin: response.ownerLogin ?? null,
    viewerOwnsAssignment: response.viewerOwnsAssignment ?? false,
    fixCICheckScope: normalizeAgentMergeCheckScope(response.fixCICheckScope),
    automaticEnrollment: normalizeAgentMergeEnrollmentAttempt(response.automaticEnrollment),
  }
}
